import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  useGetKYCByIdQuery,
  useUpdateKYCStatusMutation,
} from '@/services/KYCService';
import { KeenIcon } from '@/components';
import { useDemo1Layout, Demo1LayoutContext } from '@/layouts/demo1';
import { Modal } from '@/components/modal';
import { X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/card/card';
import { KYCStatus, KYCType } from '@/constant';
import { displayKycCountry } from '@/utils/countryName';

export default function KYCDetailsView() {
  const location = useLocation();
  const { layout } = useDemo1Layout() as unknown as Demo1LayoutContext;

  const urlSearchParams = new URLSearchParams(location.search);
  const kycId = urlSearchParams.get('kycId');
  const mode = urlSearchParams.get('mode') || 'view';

  const {
    data: kycDataResponse,
    isLoading,
    error,
    refetch: refetchKYC,
  } = useGetKYCByIdQuery(kycId || '', {
    skip: !kycId,
  });

  const [updateKYCStatus, { isLoading: isUpdatingKYC }] =
    useUpdateKYCStatusMutation();

  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<
    | KYCStatus.PENDING
    | KYCStatus.UNDER_REVIEW
    | KYCStatus.APPROVED
    | KYCStatus.REJECTED
    | KYCStatus.REQUIRES_UPDATE
    | KYCStatus.NOT_REQUIRED
    | KYCStatus.ON_HOLD
  >(KYCStatus.PENDING);
  const [kycNotes, setKycNotes] = useState('');
  const [kycNotesError, setKycNotesError] = useState('');

  // Handle both cases: data directly or wrapped in ApiResponse
  const kycData = useMemo(() => {
    if (!kycDataResponse) return null;
    // If it's an array (shouldn't happen for single item, but handle it)
    if (Array.isArray(kycDataResponse)) {
      return kycDataResponse[0] || null;
    }
    // If it has a data property (ApiResponse wrapper)
    if (kycDataResponse.data) {
      return kycDataResponse.data;
    }
    // If it's the data directly (same format as list API)
    return kycDataResponse as typeof kycDataResponse;
  }, [kycDataResponse]);

  // Decrypt encrypted fields
  // API returns plaintext account fields
  const decryptedData = kycData;

  useEffect(() => {
    if (kycData) {
      setKycStatus(
        kycData.kyc_status as
          | KYCStatus.PENDING
          | KYCStatus.UNDER_REVIEW
          | KYCStatus.APPROVED
          | KYCStatus.REJECTED
          | KYCStatus.REQUIRES_UPDATE
          | KYCStatus.NOT_REQUIRED
          | KYCStatus.ON_HOLD
      );
      setKycNotes(kycData.admin_notes || '');
    }
  }, [kycData]);

  if (!kycId) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-4 text-gray-500">
          No KYC ID provided.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-4 contrast:text-white">
          Loading KYC details...
        </div>
      </div>
    );
  }

  if (error || !kycData || !decryptedData) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-4 text-red-500">
          Failed to load KYC details. Please try again.
        </div>
      </div>
    );
  }

  const kyc = decryptedData;

  const getEntityTypeLabel = (entityType: string) => {
    switch (entityType) {
      case KYCType.HARBOR:
        return 'Harbor';
      case KYCType.CLUB:
        return 'Club';
      case KYCType.SERVICE_PROVIDER:
        return 'Service Provider';
      default:
        return entityType;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case KYCStatus.PENDING:
        return <span className="badge badge-info">Pending</span>;
      case KYCStatus.UNDER_REVIEW:
        return <span className="badge badge-warning">Under Review</span>;
      case KYCStatus.APPROVED:
        return <span className="badge badge-success">Approved</span>;
      case KYCStatus.REJECTED:
        return <span className="badge badge-danger">Rejected</span>;
      case KYCStatus.REQUIRES_UPDATE:
        return <span className="badge badge-warning">Requires Update</span>;
      case KYCStatus.NOT_REQUIRED:
        return <span className="badge badge-secondary">Not Required</span>;
      case KYCStatus.ON_HOLD:
        return <span className="badge badge-warning">On Hold</span>;
      case KYCStatus.RESUBMITTED:
        return <span className="badge badge-info">Resubmitted</span>;
      case KYCStatus.DRAFT:
        return <span className="badge badge-secondary">Not Submitted</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  return (
    <div className="kyc-details-page w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="mt-3">
            <Link
              to="/kyc-management"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              aria-label="Back to KYC management"
            >
              <KeenIcon
                icon="black-left"
                className="text-xl"
                aria-hidden="true"
              />
            </Link>
          </div>
          <header
            className={`flex-1 md:border-b border-gray-200 kyc-header ${layout.options.sidebar.collapse ? 'header-collapsed' : ''} overflow-hidden`}
          >
            <nav
              className="-mb-px flex space-x-6 overflow-x-auto"
              aria-label="Tabs"
              style={{ maxWidth: 'calc(100vw - 200px)' }}
            >
              <div
                className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1 flex-shrink-0 min-w-fit border-blue-500 text-blue-600 contrast:text-blue-400`}
              >
                KYC Details
              </div>
            </nav>
          </header>
        </div>
        <div className="py-8">
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>KYC Details</CardTitle>
                  {mode === 'edit' && (
                    <button
                      type="button"
                      onClick={() => {
                        setKycStatus(
                          kyc.kyc_status as
                            | KYCStatus.PENDING
                            | KYCStatus.UNDER_REVIEW
                            | KYCStatus.APPROVED
                            | KYCStatus.REJECTED
                            | KYCStatus.REQUIRES_UPDATE
                            | KYCStatus.NOT_REQUIRED
                            | KYCStatus.ON_HOLD
                        );
                        setKycNotes(kyc.admin_notes || '');
                        setKycNotesError('');
                        setIsUpdateStatusModalOpen(true);
                      }}
                      disabled={isUpdatingKYC}
                      className="btn btn-sm btn-outline bg-primary text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                    >
                      Update Status
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Legal Name
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.legal_name}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Entity Type
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {getEntityTypeLabel(kyc.entity_type)}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Entity Name
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.entity?.name || '—'}{' '}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Business ID
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.business_id}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      VAT ID
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.vat_id}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address Line 1
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.address_line_1}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address Line 2
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.address_line_2 || '-'}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Postal Code
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.postal_code}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      City
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.city}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      State
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.state || '-'}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Country
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.country}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Account Holder Name
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.account_holder_name}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Account Number
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.account_number}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      IBAN
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.iban}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      BIC
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.bic}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Bank Name
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.bank_name}
                    </p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      KYC Status
                    </div>
                    <p className="mt-1">{getStatusBadge(kyc.kyc_status)}</p>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Admin Notes
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {kyc.admin_notes || '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Update Status Modal */}
      <Modal
        open={isUpdateStatusModalOpen}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setKycNotesError('');
        }}
        className="!flex items-center justify-center"
      >
        <div className="bg-white dark:bg-gray-800 contrast:bg-[#101010]  p-6 w-full max-w-md mx-auto rounded-lg shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Update KYC Status
            </h2>
            <button
              type="button"
              onClick={() => {
                setIsUpdateStatusModalOpen(false);
                setKycNotesError('');
              }}
              aria-label="Close update KYC status dialog"
              title="Close"
              className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 contrast:text-white"
            >
              <X className="h-5 w-5" aria-hidden="true" focusable="false" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="kyc-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Status
              </label>
              <select
                id="kyc-status"
                value={kycStatus}
                onChange={(e) =>
                  setKycStatus(
                    e.target.value as
                      | KYCStatus.PENDING
                      | KYCStatus.UNDER_REVIEW
                      | KYCStatus.APPROVED
                      | KYCStatus.REJECTED
                      | KYCStatus.REQUIRES_UPDATE
                      | KYCStatus.NOT_REQUIRED
                      | KYCStatus.ON_HOLD
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500  dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={KYCStatus.PENDING}>Pending</option>
                <option value={KYCStatus.UNDER_REVIEW}>Under Review</option>
                <option value={KYCStatus.APPROVED}>Approved</option>
                <option value={KYCStatus.REJECTED}>Rejected</option>
                <option value={KYCStatus.NOT_REQUIRED}>Not Required</option>
                <option value={KYCStatus.REQUIRES_UPDATE}>
                  Requires Update
                </option>
                <option value={KYCStatus.ON_HOLD}>On Hold</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="kyc-notes"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                id="kyc-notes"
                value={kycNotes}
                onChange={(e) => {
                  setKycNotes(e.target.value);
                  if (e.target.value.trim()) {
                    setKycNotesError('');
                  }
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 resize-none dark:bg-gray-700 text-gray-900 dark:text-white ${
                  kycNotesError
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                }`}
                placeholder="Enter admin notes..."
              />
              {kycNotesError && (
                <p className="mt-1 text-xs text-red-500">{kycNotesError}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setIsUpdateStatusModalOpen(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!kycId) return;

                  if (!kycNotes.trim()) {
                    setKycNotesError('Notes are required');
                    return;
                  }

                  try {
                    await updateKYCStatus({
                      kycId,
                      data: {
                        kyc_status: kycStatus,
                        admin_notes: kycNotes,
                      },
                    }).unwrap();
                    refetchKYC();
                    setIsUpdateStatusModalOpen(false);
                  } catch (error) {
                    console.error('Failed to update KYC status:', error);
                  }
                }}
                disabled={isUpdatingKYC || !kycId}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingKYC ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export { KYCDetailsView };
